// ============================================================================
// FORMATO REGIONAL EN HIGHCHARTS
// ============================================================================
// Highcharts formatea sus propios números y fechas con `Highcharts.lang`. En
// Highcharts 12 la clave es `lang.locale`: de ella derivan el separador decimal,
// el de miles y los nombres de mes y día. Como nunca se configuró, TODOS los
// gráficos de la app mostraban:
//
//   - punto decimal fijo, sin importar el Formato Regional del proyecto
//     (`tooltip.valueDecimals`, `labels.format: '{value} l/s'`, ~29 puntos entre
//     PanelGraficos, CaudalDetails, GraficoAcumulado, CalendarioRiegos y
//     DetailsVpd);
//   - meses y días de la semana EN INGLÉS ("Monday", "Jan"), conviviendo en la
//     misma pantalla con las fechas en español que produce Day.js. Ese era un
//     bug visible ya antes del Formato Regional por proyecto.
//
// Este módulo resuelve las dos cosas con un único `setOptions({ lang })`
// derivado del Formato Regional del proyecto, en vez de parchear gráfico por
// gráfico.
//
// Ojo con el fallback implícito
// -----------------------------
// Highcharts 12 resuelve el locale en tres pasos: (1) `lang.locale`, (2) el
// atributo `lang` del `<html>` más cercano, (3) el locale del navegador. Como
// applyProjectLocale() ya escribe `<html lang>`, el paso 2 haría *casi* todo el
// trabajo solo — pero de forma implícita y frágil: cualquiera que tocara ese
// atributo movería el formato de los gráficos sin saber por qué. Acá se fija el
// paso 1, que tiene prioridad y deja la intención declarada en el código.
//
// Por qué el patrón de "registro" y no un import directo
// ------------------------------------------------------
// `applyProjectLocale()` corre en main.js, antes del mount. Si este módulo
// importara `highcharts/highstock` de forma estática, arrastraría TODO Highcharts
// al chunk inicial de la app, cuando hoy viaja en los chunks perezosos de las
// pantallas de gráficos. Por eso los componentes se registran a sí mismos
// (`registerHighcharts`) y acá sólo se guarda la referencia.
//
// Como todos los componentes importan `highcharts/highstock` (el MISMO singleton
// de módulo ES), registrar una vez alcanza para todos los gráficos.

import { resolveLocale, decimalSeparatorFor, groupSeparatorFor } from './localeConfig';
import { formatNumber } from './formatProjectData';

// Locale que se le declara a HIGHCHARTS, que NO es el del proyecto en ninguno
// de los dos casos. Highcharts 12 deriva sus formatos automáticos de
// `lang.locale`, y ni es-CL ni en-US usan reloj de 24 en CLDR:
//
//   Intl.DateTimeFormat('es-CL', { timeStyle: 'medium' })  ->  h12, "6:23:18 p. m."
//   Intl.DateTimeFormat('en-US', ...)                      ->  h12, "6:23:18 PM"
//   Intl.DateTimeFormat('es-ES', ...)                      ->  h23, "18:23:18"
//   Intl.DateTimeFormat('en-GB', ...)                      ->  h23, "18:23:18"
//
// Este archivo afirmaba que "es-CL ya usa 24h" y era falso: los tooltips de los
// gráficos mostraban "06:23:18 p. m." mientras la cabecera de la MISMA pantalla
// decía "13:39:45". Se detectó recién al leer un tooltip real; el mapeo de
// en-US, que sí estaba puesto por esta razón, tapaba la mitad del problema.
//
// Se elige el locale hermano que comparte idioma y orden de campos pero usa
// reloj de 24 (ver la decisión de producto en utils/formatProjectDate.js):
//
//   es-CL -> es-ES  mismos nombres de mes y día en español, fecha día-primero
//   en-US -> en-GB  mismos nombres en inglés
//
// CONTRAPARTIDA ACEPTADA en en-US: las fechas automáticas de los ejes quedan en
// orden británico (DD/MM) en vez del estadounidense (MM/DD). Se prefirió eso
// antes que declarar `dateTimeLabelFormats` completos con `hour12: false`.
// En es-CL no hay contrapartida: es-ES ya es día-primero.
//
// No afecta a los números: decimalPoint/thousandsSep se derivan del locale REAL
// del proyecto, no de éste (ver buildHighchartsLang).
const HIGHCHARTS_LOCALES = {
  'es-CL': 'es-ES',
  'en-US': 'en-GB'
};

function highchartsLocaleFor(locale) {
  const l = resolveLocale(locale);
  return HIGHCHARTS_LOCALES[l] || l;
}

// Instancia de Highcharts registrada por el primer componente de gráficos que
// se cargue (null mientras no se haya abierto ninguna pantalla con gráficos).
let highcharts = null;

// Último locale pedido. Se guarda para poder aplicarlo en cuanto aparezca la
// instancia: el locale se resuelve al arrancar la app, pero los gráficos se
// cargan mucho después (rutas perezosas).
let currentLocale = null;

/**
 * Construye el objeto `lang` de Highcharts para un locale.
 * Exportado sobre todo para poder testearlo sin cargar Highcharts.
 *
 * NO se declaran `months` / `shortMonths` / `weekdays` a propósito: en
 * Highcharts 12 su default es `undefined`, que significa «derivalos de
 * `lang.locale`». Escribirlos a mano los desincronizaría de las etiquetas
 * automáticas del eje datetime —que sí salen de Intl— y terminaríamos con
 * "Jul" en el tooltip y "jul" en el eje del MISMO gráfico.
 *
 * `decimalPoint` y `thousandsSep` también derivan de `lang.locale`, pero se
 * fijan explícitos: son el motivo por el que existe este módulo y conviene que
 * queden greppables desde el código.
 */
export function buildHighchartsLang(locale) {
  const l = resolveLocale(locale);
  return {
    // OJO: no es `l`. Ver HIGHCHARTS_LOCALES arriba (en-US -> en-GB por el 24h).
    locale: highchartsLocaleFor(l),
    decimalPoint: decimalSeparatorFor(l),
    thousandsSep: groupSeparatorFor(l)
  };
}

/**
 * Aplica el locale a Highcharts. Si todavía no hay ninguna pantalla de gráficos
 * cargada, sólo lo recuerda: `registerHighcharts()` lo aplicará al llegar.
 *
 * `setOptions` hace merge profundo, así que esto NO pisa las otras claves de
 * `lang` que configuran el tema (resetZoom) ni PanelGraficos (menú de
 * exportación): son textos fijos en español, no dependen del locale.
 *
 * @returns {string} el locale vigente para los gráficos
 */
export function applyHighchartsLocale(locale) {
  currentLocale = resolveLocale(locale);
  if (highcharts && typeof highcharts.setOptions === 'function') {
    highcharts.setOptions({ lang: buildHighchartsLang(currentLocale) });
  }
  return currentLocale;
}

/**
 * Registra la instancia de Highcharts del componente y le aplica el locale
 * vigente. Idempotente: llamarla desde los 5 componentes de gráficos es
 * inofensivo (todos comparten el mismo singleton).
 *
 * OJO: los gráficos YA renderizados no releen `lang`; hay que re-crearlos. En la
 * práctica no hace falta porque cambiar de proyecto recarga la página
 * (stores/proyecto.js), pero para eso está el evento 'hb:locale-changed'.
 */
export function registerHighcharts(Highcharts) {
  if (!Highcharts || typeof Highcharts.setOptions !== 'function') return;
  highcharts = Highcharts;
  if (currentLocale) {
    Highcharts.setOptions({ lang: buildHighchartsLang(currentLocale) });
  }
}

/**
 * Formateador de etiquetas de eje CON UNIDAD, para reemplazar
 * `labels: { format: '{value} bar' }`.
 *
 * Por qué hace falta
 * ------------------
 * `lang.decimalPoint` NO alcanza cuando el eje declara un `format`. En el
 * motor de plantillas de Highcharts, un `{value}` pelado se resuelve como
 * `String(valor)` —o sea, punto decimal siempre—; sólo pasa por `numberFormat`
 * si lleva especificador (`{value:.1f}`). Por eso los ejes SIN `format`
 * mostraban "0,25" correctamente mientras el de al lado, con
 * `format: '{value} bar'`, mostraba "0.3 bar" en el mismo proyecto es-CL.
 *
 * No se usó `{value:.1f}` porque fija la cantidad de decimales para todo el
 * eje: los ticks enteros pasarían a verse "20052.0". Este formateador usa el
 * `formatNumber` de la app, que además respeta el locale del PROYECTO y no
 * sólo el `lang` que se le declaró a Highcharts.
 *
 * @param {string} [unidad] sufijo tal cual va pegado al número (' bar', 'uS/cm')
 * @param {object} [options] opciones de formatNumber (decimals, grouping…)
 */
export function axisLabelFormatter(unidad = '', options = {}) {
  // `function` y no flecha: Highcharts invoca el formateador con `this` = el tick.
  return function () {
    return `${formatNumber(this.value, undefined, options)}${unidad}`;
  };
}

/** Locale aplicado a los gráficos (null si todavía no se aplicó ninguno). */
export function getHighchartsLocale() {
  return currentLocale;
}

export default {
  buildHighchartsLang,
  axisLabelFormatter,
  applyHighchartsLocale,
  registerHighcharts,
  getHighchartsLocale
};
